import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Upload, BadgeCheck, ThumbsUp } from "lucide-react";
import { StarRating } from "./StarRating";
import api from "../lib/api";

export const ReviewSection = ({ productSlug }) => {
    const [data, setData] = useState({ reviews: [], summary: { average: 0, total: 0, distribution: {} } });
    const [sort, setSort] = useState("newest");
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ customer_name: "", customer_email: "", rating: 5, title: "", body: "", photo_url: "", website: "" });
    const [eligibility, setEligibility] = useState(null); // null | "checking" | true | false

    // Live check eligibility when email is entered
    useEffect(() => {
        const email = form.customer_email.trim();
        if (!email || !email.includes("@")) {
            setEligibility(null);
            return;
        }
        setEligibility("checking");
        const handle = setTimeout(() => {
            api.get("/reviews/eligibility", { params: { email, product_slug: productSlug } })
                .then((r) => setEligibility(r.data.verified))
                .catch(() => setEligibility(null));
        }, 500);
        return () => clearTimeout(handle);
    }, [form.customer_email, productSlug]);

    const load = () => {
        api.get(`/reviews/${productSlug}`, { params: { sort } }).then((r) => setData(r.data));
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [productSlug, sort]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || form.body.trim().length < 10) {
            toast.error("Please add a review title and at least 10 characters of feedback.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post("/reviews", {
                ...form,
                product_slug: productSlug,
                rating: Number(form.rating),
                customer_email: form.customer_email || undefined,
                customer_name: form.customer_name || undefined,
            });
            if (res.data.id === "") {
                // Honeypot triggered — silently succeed
            } else {
                toast.success(
                    res.data.verified_purchase
                        ? "Verified-purchase review submitted! Awaiting moderator approval."
                        : "Review submitted! It will appear after moderator approval."
                );
            }
            setForm({ customer_name: "", customer_email: "", rating: 5, title: "", body: "", photo_url: "", website: "" });
            setEligibility(null);
            setShowForm(false);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Failed to submit review";
            toast.error(msg);
        }
        setSubmitting(false);
    };

    const markHelpful = async (id) => {
        try {
            await api.post(`/reviews/${id}/helpful`);
            setData((d) => ({
                ...d,
                reviews: d.reviews.map((r) => r.id === id ? { ...r, helpful_count: r.helpful_count + 1 } : r),
            }));
        } catch {
            toast.error("Could not record vote");
        }
    };

    const max = Math.max(...Object.values(data.summary.distribution || {}), 1);

    return (
        <section className="mt-20" data-testid="reviews-section">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                <div>
                    <span className="eyebrow">// Verified Reviews</span>
                    <h2 className="font-display text-2xl md:text-4xl font-extrabold text-white tracking-tight mt-2">
                        Customer Reviews
                    </h2>
                </div>
                <button onClick={() => setShowForm((v) => !v)} className="btn-neon" data-testid="leave-review-button">
                    <Star size={14} /> Leave a Review
                </button>
            </div>

            {/* Summary */}
            <div className="glass-elevated rounded-2xl p-6 md:p-8 mb-8 grid md:grid-cols-3 gap-6">
                <div className="text-center md:text-left">
                    <div className="font-display text-6xl font-extrabold text-white">{data.summary.average.toFixed(1)}</div>
                    <StarRating value={data.summary.average} size={18} />
                    <div className="text-xs font-mono text-[var(--text-muted)] uppercase tracking-widest mt-2">
                        Based on {data.summary.total} review{data.summary.total !== 1 ? "s" : ""}
                    </div>
                </div>
                <div className="md:col-span-2 space-y-1.5">
                    {[5, 4, 3, 2, 1].map((star) => {
                        const count = data.summary.distribution?.[String(star)] || 0;
                        const pct = (count / max) * 100;
                        return (
                            <div key={star} className="flex items-center gap-3 text-sm">
                                <span className="text-[var(--text-secondary)] w-3 font-mono">{star}</span>
                                <Star size={12} className="star-filled fill-current" />
                                <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-[var(--gold)] to-[var(--gold-bright)]" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs font-mono text-[var(--text-muted)] w-8 text-right">{count}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Review Form */}
            {showForm && (
                <form onSubmit={submit} className="glass-elevated rounded-2xl p-6 mb-8 space-y-4" data-testid="review-form">
                    <div className="grid md:grid-cols-2 gap-4">
                        <input
                            value={form.customer_name}
                            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                            placeholder="Your name (optional)"
                            maxLength={80}
                            className="px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]"
                            data-testid="review-name-input"
                        />
                        <div className="flex items-center gap-3 px-4 py-2.5 bg-black/40 border border-white/10 rounded-full">
                            <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">Rating</span>
                            {[1, 2, 3, 4, 5].map((r) => (
                                <button
                                    key={r}
                                    type="button"
                                    onClick={() => setForm({ ...form, rating: r })}
                                    data-testid={`rating-star-${r}`}
                                    aria-label={`Rate ${r} star${r > 1 ? "s" : ""}`}
                                >
                                    <Star size={18} className={r <= form.rating ? "star-filled fill-current" : "star-empty"} />
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Honeypot — hidden from real users via aria-hidden + tabIndex + offscreen */}
                    <input
                        type="text"
                        name="website"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        style={{ position: "absolute", left: "-10000px", width: "1px", height: "1px", opacity: 0 }}
                    />
                    <input
                        required
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="Review title *"
                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]"
                        data-testid="review-title-input"
                    />
                    <div className="relative">
                        <input
                            type="email"
                            value={form.customer_email}
                            onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                            placeholder="Email (optional — auto-detects verified purchases)"
                            className="w-full px-4 py-2.5 pr-32 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]"
                            data-testid="review-email-input"
                        />
                        {eligibility === "checking" && (
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Checking...
                            </span>
                        )}
                        {eligibility === true && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-mono uppercase tracking-widest border border-emerald-500/40 bg-emerald-500/10 text-emerald-300" data-testid="verified-purchase-indicator">
                                <BadgeCheck size={10} /> Verified
                            </span>
                        )}
                        {eligibility === false && (
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                No prior order
                            </span>
                        )}
                    </div>
                    <textarea
                        required
                        rows={4}
                        value={form.body}
                        onChange={(e) => setForm({ ...form, body: e.target.value })}
                        placeholder="Your experience with this product... *"
                        className="w-full px-4 py-3 bg-black/40 border border-white/10 rounded-2xl text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)] resize-none"
                        data-testid="review-body-input"
                    />
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-black/40 border border-white/10 rounded-full">
                        <Upload size={14} className="text-[var(--text-muted)]" />
                        <input
                            value={form.photo_url}
                            onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                            placeholder="Photo URL (optional)"
                            className="flex-1 bg-transparent text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none"
                            data-testid="review-photo-input"
                        />
                    </div>
                    <div className="flex gap-3">
                        <button type="submit" disabled={submitting} className="btn-solid" data-testid="review-submit-button">
                            {submitting ? "Submitting..." : "Submit Review"}
                        </button>
                        <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
                    </div>
                    <p className="text-xs text-[var(--text-muted)]">Reviews require moderator approval before they appear publicly.</p>
                </form>
            )}

            {/* Sort + Verified filter */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">Sort:</span>
                {[
                    { v: "newest", l: "Newest" },
                    { v: "highest", l: "Highest" },
                    { v: "helpful", l: "Most helpful" },
                ].map((s) => (
                    <button
                        key={s.v}
                        onClick={() => setSort(s.v)}
                        className={`px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest border transition-all ${
                            sort === s.v
                                ? "border-[var(--gold)] text-[var(--gold)]"
                                : "border-white/10 text-[var(--text-secondary)]"
                        }`}
                        data-testid={`sort-reviews-${s.v}`}
                    >
                        {s.l}
                    </button>
                ))}
                <div className="w-px h-4 bg-white/10 mx-1" />
                <button
                    onClick={() => setVerifiedOnly((v) => !v)}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest border transition-all ${
                        verifiedOnly
                            ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                            : "border-white/10 text-[var(--text-secondary)]"
                    }`}
                    data-testid="verified-only-filter"
                >
                    <BadgeCheck size={11} /> Verified only
                    <span className="ml-1 opacity-70">({data.reviews.filter((r) => r.verified_purchase).length})</span>
                </button>
            </div>

            {/* Reviews list */}
            <div className="space-y-4" data-testid="reviews-list">
                {(() => {
                    const visible = verifiedOnly
                        ? data.reviews.filter((r) => r.verified_purchase)
                        : data.reviews;
                    if (visible.length === 0) {
                        return (
                            <div className="glass rounded-2xl p-8 text-center text-[var(--text-secondary)]">
                                {verifiedOnly
                                    ? "No verified-purchase reviews yet."
                                    : "No reviews yet. Be the first."}
                            </div>
                        );
                    }
                    return visible.map((r) => (
                        <div key={r.id} className="glass rounded-2xl p-5" data-testid={`review-card-${r.id}`}>
                            <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <StarRating value={r.rating} size={14} />
                                        {r.verified_purchase && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
                                                <BadgeCheck size={10} /> Verified
                                            </span>
                                        )}
                                    </div>
                                    <h4 className="font-display font-bold text-white">{r.title}</h4>
                                    <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                                        {r.customer_name} · {new Date(r.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{r.body}</p>
                            {r.photo_url && (
                                <img src={r.photo_url} alt="Customer" className="mt-3 max-h-32 rounded-lg border border-white/10" />
                            )}
                            <button
                                onClick={() => markHelpful(r.id)}
                                className="mt-3 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--gold)]"
                                data-testid={`helpful-button-${r.id}`}
                            >
                                <ThumbsUp size={12} /> Helpful ({r.helpful_count})
                            </button>
                        </div>
                    ));
                })()}
            </div>
        </section>
    );
};
